import type { JsonKind, JsonPath, JsonValue } from '../types';
import { inferType } from './json-path';

export interface FlatRow {
  path: JsonPath;
  rowKey: string;
  depth: number;
  keyLabel: string;
  keyEditable: boolean;
  nodeType: JsonKind;
  isRoot: boolean;
  childCount?: number;
  isMatched?: boolean;
}

export const getRowKey = (path: JsonPath): string => (path.length === 0 ? 'root' : JSON.stringify(path));

interface FlattenOptions {
  collapsedPaths: Set<string>;
  searchQuery?: string;
}

const stringifyForSearch = (value: JsonValue): string => {
  if (value === null) return 'null';
  if (typeof value === 'object') return '';
  return String(value);
};

const matchesSearch = (value: JsonValue, keyLabel: string, path: JsonPath, query: string): boolean => {
  const pathLabel = path.map((segment) => String(segment)).join('.').toLowerCase();
  return (
    keyLabel.toLowerCase().includes(query) ||
    pathLabel.includes(query) ||
    stringifyForSearch(value).toLowerCase().includes(query)
  );
};

export interface SearchMatch {
  path: JsonPath;
  rowKey: string;
}

/**
 * Finds every node in the ENTIRE tree whose key, path, or primitive value
 * matches the query — regardless of collapse state. This must be a separate
 * walk from `flattenTree`: that one stops descending into collapsed nodes
 * (by design, for virtualization), so it can never find a match hidden under
 * a collapsed ancestor. Large documents auto-collapse below `autoCollapseDepth`
 * by default, so without this separate pass, search would silently miss
 * almost everything in a big document.
 */
export const findSearchMatches = (value: JsonValue, searchQuery: string): SearchMatch[] => {
  const trimmedQuery = searchQuery.trim().toLowerCase();
  if (!trimmedQuery) return [];

  const matches: SearchMatch[] = [];

  const walk = (nodeValue: JsonValue, path: JsonPath, keyLabel: string) => {
    if (matchesSearch(nodeValue, keyLabel, path, trimmedQuery)) {
      matches.push({ path, rowKey: getRowKey(path) });
    }

    const nodeType = inferType(nodeValue);

    if (nodeType === 'object') {
      Object.entries(nodeValue as Record<string, JsonValue>).forEach(([childKey, childValue]) => {
        walk(childValue, [...path, childKey], childKey);
      });
    }

    if (nodeType === 'array') {
      (nodeValue as JsonValue[]).forEach((childValue, index) => {
        walk(childValue, [...path, index], `[${index}]`);
      });
    }
  };

  walk(value, [], 'root');
  return matches;
};

/** Row keys of every ancestor of `path`, from root down to (but excluding) `path` itself. */
export const getAncestorRowKeys = (path: JsonPath): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < path.length; index += 1) {
    keys.push(getRowKey(path.slice(0, index)));
  }
  return keys;
};

/**
 * Collapses every foldable (object/array) node in the tree except the given
 * row keys. Used to declutter the tree when jumping to a search match: only
 * that match's ancestor chain should be expanded, not every branch anyone
 * has ever expanded or that a previous match happened to reveal.
 */
export const collapseAllExcept = (value: JsonValue, keepExpandedRowKeys: Set<string>): Set<string> => {
  const collapsed = new Set<string>();

  const walk = (nodeValue: JsonValue, path: JsonPath) => {
    const nodeType = inferType(nodeValue);
    if (nodeType !== 'object' && nodeType !== 'array') return;

    const rowKey = getRowKey(path);
    if (!keepExpandedRowKeys.has(rowKey)) {
      collapsed.add(rowKey);
    }

    if (nodeType === 'object') {
      Object.entries(nodeValue as Record<string, JsonValue>).forEach(([key, child]) => walk(child, [...path, key]));
    } else {
      (nodeValue as JsonValue[]).forEach((child, index) => walk(child, [...path, index]));
    }
  };

  walk(value, []);
  return collapsed;
};

/**
 * Flattens the JSON tree (skipping children of collapsed nodes) into an
 * ordered array of visible rows. This is what makes virtualization possible:
 * only the array is walked once per data/collapse change (O(visible nodes),
 * cheap even at 10k+ nodes since collapsed subtrees are never visited), and
 * the renderer just indexes into it.
 */
export const flattenTree = (value: JsonValue, { collapsedPaths, searchQuery }: FlattenOptions): FlatRow[] => {
  const rows: FlatRow[] = [];
  const trimmedQuery = searchQuery?.trim().toLowerCase();

  const walk = (
    nodeValue: JsonValue,
    path: JsonPath,
    depth: number,
    keyLabel: string,
    keyEditable: boolean,
    isRoot: boolean,
  ) => {
    const nodeType = inferType(nodeValue);
    const rowKey = getRowKey(path);
    const isMatched = trimmedQuery ? matchesSearch(nodeValue, keyLabel, path, trimmedQuery) : undefined;

    rows.push({
      path,
      rowKey,
      depth,
      keyLabel,
      keyEditable,
      nodeType,
      isRoot,
      childCount:
        nodeType === 'object'
          ? Object.keys(nodeValue as Record<string, JsonValue>).length
          : nodeType === 'array'
            ? (nodeValue as JsonValue[]).length
            : undefined,
      isMatched,
    });

    if (collapsedPaths.has(rowKey)) return;

    if (nodeType === 'object') {
      Object.entries(nodeValue as Record<string, JsonValue>).forEach(([childKey, childValue]) => {
        walk(childValue, [...path, childKey], depth + 1, childKey, true, false);
      });
    }

    if (nodeType === 'array') {
      (nodeValue as JsonValue[]).forEach((childValue, index) => {
        walk(childValue, [...path, index], depth + 1, `[${index}]`, false, false);
      });
    }
  };

  walk(value, [], 0, 'root', false, true);
  return rows;
};

/**
 * Auto-collapse strategy for large documents (mirrors vanilla-jsoneditor's
 * approach of not fully expanding huge documents by default): if the total
 * node count exceeds `totalNodeCountThreshold`, every object/array at or
 * below `autoCollapseDepth` starts collapsed. Only used to seed the initial
 * `collapsedPaths` state — the user's manual expand/collapse afterward
 * always takes precedence.
 */
export const computeInitialCollapsedPaths = (
  value: JsonValue,
  { autoCollapseDepth, totalNodeCountThreshold }: { autoCollapseDepth: number; totalNodeCountThreshold: number },
): Set<string> => {
  const collapsed = new Set<string>();
  let nodeCount = 0;

  const count = (nodeValue: JsonValue) => {
    nodeCount += 1;
    const nodeType = inferType(nodeValue);
    if (nodeType === 'object') Object.values(nodeValue as Record<string, JsonValue>).forEach(count);
    if (nodeType === 'array') (nodeValue as JsonValue[]).forEach(count);
  };
  count(value);

  if (nodeCount <= totalNodeCountThreshold) return collapsed;

  const markCollapsed = (nodeValue: JsonValue, path: JsonPath, depth: number) => {
    const nodeType = inferType(nodeValue);
    if (nodeType !== 'object' && nodeType !== 'array') return;

    if (depth >= autoCollapseDepth) {
      collapsed.add(getRowKey(path));
      return;
    }

    if (nodeType === 'object') {
      Object.entries(nodeValue as Record<string, JsonValue>).forEach(([key, child]) =>
        markCollapsed(child, [...path, key], depth + 1),
      );
    } else {
      (nodeValue as JsonValue[]).forEach((child, index) => markCollapsed(child, [...path, index], depth + 1));
    }
  };
  markCollapsed(value, [], 0);

  return collapsed;
};
